import { useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';

import Card from '@/components/Card';
import { Body, Small } from '@/components/Typography';
import {
  JobHoldLog,
  JobHoldLogAction,
  Pagination,
  fetchWorkerJobHoldLogs,
} from '@/services/jobHoldLogsAPI';

type Props = {
  workerId: number;
};

const ACTION_LABELS: Record<JobHoldLogAction, string> = {
  created: 'Created',
  released: 'Released',
  expired: 'Expired',
  claimed: 'Claimed',
};

const ACTION_COLORS: Record<
  JobHoldLogAction,
  'primary' | 'default' | 'warning' | 'success'
> = {
  created: 'primary',
  released: 'default',
  expired: 'warning',
  claimed: 'success',
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatAge = (ageSeconds: number | null) => {
  if (ageSeconds === null) return '—';
  const minutes = Math.floor(ageSeconds / 60);
  const seconds = ageSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const JobHoldLogSection = ({ workerId }: Props) => {
  const [rows, setRows] = useState<JobHoldLog[] | null>(null);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    (targetPage: number) => {
      fetchWorkerJobHoldLogs(workerId, { page: targetPage, per_page: 25 })
        .then((res) => {
          setRows(res.data);
          setPagination(res.pagination);
          setError(null);
        })
        .catch((e: unknown) => {
          const msg =
            e instanceof Error ? e.message : 'Failed to load shift hold logs';
          setError(msg);
        });
    },
    [workerId],
  );

  useEffect(() => {
    load(page);
  }, [load, page]);

  if (error) {
    return (
      <Card title="Shift Hold Activity">
        <Card.Section>
          <Body color="lighter">{error}</Body>
        </Card.Section>
      </Card>
    );
  }

  if (rows === null) {
    return (
      <Card title="Shift Hold Activity">
        <Card.Section>
          <Body>Loading...</Body>
        </Card.Section>
      </Card>
    );
  }

  if (rows.length === 0 && page === 1) {
    return (
      <Card title="Shift Hold Activity">
        <Card.Section>
          <Body color="lighter">
            No hold activity recorded yet. Only activity since audit logging was
            enabled is shown.
          </Body>
        </Card.Section>
      </Card>
    );
  }

  return (
    <Card title="Shift Hold Activity">
      <Card.Section>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px',
            }}
          >
            <thead>
              <tr style={{ textAlign: 'left', color: '#6B7280' }}>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>When</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Shift</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Event</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>Source</th>
                <th style={{ padding: '8px 12px', fontWeight: 600 }}>
                  Hold age
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid #E5E7EB' }}>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    <Small color="lighter">{formatDate(r.created_at)}</Small>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Body>
                      #{r.job_id}
                      {r.job_title ? ` — ${r.job_title}` : ''}
                    </Body>
                    {r.customer_name && (
                      <Small color="lighter"> {r.customer_name}</Small>
                    )}
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Chip
                      label={ACTION_LABELS[r.action] ?? r.action}
                      color={ACTION_COLORS[r.action] ?? 'default'}
                      size="small"
                    />
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Body>{r.source ?? '—'}</Body>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <Body>{formatAge(r.age_seconds)}</Body>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination && pagination.total_pages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginTop: '12px',
            }}
          >
            <Button
              size="small"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Small color="lighter">
              Page {pagination.page} of {pagination.total_pages} (
              {pagination.total} events)
            </Small>
            <Button
              size="small"
              disabled={page >= pagination.total_pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </Card.Section>
    </Card>
  );
};

export default JobHoldLogSection;
